/*
  CR B8 — "Allow the business owner to change the site's colour
  combination/theme."

  A curated set of preset palettes rather than a raw colour picker, so
  every combination stays readable (the public template relies on
  --brand / --brand-2 / --accent / --accent-d having enough contrast
  against --paper/--panel). Stored as a single short key in
  business_site_data.theme_color; "" / unknown falls back to the
  template's built-in default palette (classic-navy).
*/
export const THEME_PRESETS = [
    { key: "classic-navy", label: "Classic Navy", brand: "#1f2a3a", brand2: "#3a4a63", accent: "#b8873a", accentD: "#966d2c" },
    { key: "forest", label: "Forest Green", brand: "#1d3b2a", brand2: "#2f5c43", accent: "#c9a24b", accentD: "#a3812f" },
    { key: "burgundy", label: "Burgundy", brand: "#3b1620", brand2: "#5c2634", accent: "#c9974b", accentD: "#a3782f" },
    { key: "teal", label: "Deep Teal", brand: "#123b3f", brand2: "#1f5c62", accent: "#d9a441", accentD: "#b5822a" },
    { key: "charcoal", label: "Charcoal", brand: "#22252a", brand2: "#3c4048", accent: "#4a90e2", accentD: "#3573bd" },
    { key: "royal", label: "Royal Purple", brand: "#2c1f3b", brand2: "#453160", accent: "#d9a441", accentD: "#b5822a" },
];

/* ------------------------------------------------------------------
   Custom colours (Political Website). A custom theme is stored in the same
   theme_color column as a preset key, as  "custom:<brand>:<accent>"
   e.g. "custom:#0a3d62:#e58e26"  (22 chars, fits VARCHAR(40)).
   Only the two colours are chosen; the lighter brand shade and darker accent
   shade the templates also use are derived so any pick stays coherent.
   Preset behaviour below is unchanged.
------------------------------------------------------------------ */
const CUSTOM_RE = /^custom:(#[0-9a-f]{6}):(#[0-9a-f]{6})$/i;

export function isCustomTheme(key) {
    return typeof key === "string" && CUSTOM_RE.test(key);
}

export function parseCustomTheme(key) {
    const m = typeof key === "string" ? key.match(CUSTOM_RE) : null;
    return m ? { brand: m[1].toLowerCase(), accent: m[2].toLowerCase() } : null;
}

export function buildCustomTheme(brand, accent) {
    return `custom:${String(brand).toLowerCase()}:${String(accent).toLowerCase()}`;
}

function hexToRgb(h) {
    const n = parseInt(h.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(rgb) {
    return "#" + rgb.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("");
}

function mix(hex, other, t) {
    const a = hexToRgb(hex);
    const b = hexToRgb(other);
    return rgbToHex(a.map((v, i) => v + (b[i] - v) * t));
}

/** WCAG contrast ratio of white text on this colour (>= 4.5 is comfortable for body text). */
export function contrastWithWhite(hex) {
    const [r, g, b] = hexToRgb(hex).map((v) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 1.05 / (0.2126 * r + 0.7152 * g + 0.0722 * b + 0.05);
}

export function getThemeVars(key) {
    const custom = parseCustomTheme(key);
    if (custom) {
        return {
            key,
            label: "Custom",
            brand: custom.brand,
            brand2: mix(custom.brand, "#ffffff", 0.2),
            accent: custom.accent,
            accentD: mix(custom.accent, "#000000", 0.18),
        };
    }
    return THEME_PRESETS.find((t) => t.key === key) || THEME_PRESETS[0];
}